/**
 * CommunityMessagesList — the scrollable messages region of `CommunityPage`.
 * Wraps a `VirtualList<CommunityChatMessageEntity>` in `reversed` mode and
 * dispatches each item through `CommunityMessageItem`.
 *
 * Flutter source of truth: `messagesList`
 * (`community_screen.dart:1381-1393`) — a `ScrollablePositionedList.builder(
 * reverse: true)` that puts the newest message at the bottom of the viewport,
 * and `messageItem` (`:1474-1494`) which builds each row with
 * `messageTime = DateUtils.getChatDate(createdTimestamp)`.
 *
 * ----------------------------------------------------------------------
 * Index-space note (load-bearing, mirrors `ChatMessagesList`):
 *
 * `useCommunity` returns `messages` **newest-first** (lower index = newer;
 * sorted desc by `createdTimestamp`). `VirtualList(reversed)` expects items in
 * **chronological** order (oldest at index 0, newest at the end) — items render
 * top-to-bottom and the viewport is anchored to the bottom. This list therefore
 * feeds `VirtualList` the array reversed (`[...messages].reverse()`).
 * ----------------------------------------------------------------------
 *
 * The "user is at the bottom" signal (Flutter's bottom-index tracking at
 * `community_screen.dart:199-248`) is forwarded directly from `VirtualList`'s
 * `onScrolledToBottom` / `onScrolledAwayFromBottom` props to the hook's
 * `onScrolledToBottom` / `onScrolledAway`. The top-sentinel "load older" page
 * (`:241-246`) maps to `onReachedEnd → onLoadMore`.
 *
 * Imperative handle (`CommunityMessagesListHandle`): forwards `scrollToBottom`
 * (snap to the NEWEST message — Flutter `_itemScrollController.scrollTo(index:
 * 0, …)` at `community_screen.dart:539-543`, used post-send and on the unread
 * pill tap) and `scrollToTop` (snap to the OLDEST message) to the underlying
 * `VirtualList`. In the reversed list the newest message anchors the bottom, so
 * "go to newest" is `scrollToBottom`.
 */

import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { VirtualList } from '@/presentation/components/common/virtualList';
import type { VirtualListHandle } from '@/presentation/components/common/virtualList';
import type { QaAttrOptions } from '@/presentation/utils/qaAttr';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { CommunityMessageItem } from './CommunityMessageItem';

/**
 * Imperative handle exposed by `CommunityMessagesList`. The host page drives it
 * from the post-send auto-scroll (`scrollToTopToken`) and the unread-pill tap.
 * `scrollToBottom` snaps to the newest message (Flutter `scrollTo(index: 0)`);
 * `scrollToTop` snaps to the oldest.
 */
export interface CommunityMessagesListHandle {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  scrollToTop: (behavior?: ScrollBehavior) => void;
}

export interface CommunityMessagesListProps {
  /**
   * Optional `data-qa-*` set spread onto the underlying `VirtualList` root so
   * the qa-tester can scope to the list. Defaults to `community-messages` when
   * omitted. Rows carry their own `community-message-<id>` ids.
   */
  qa?: QaAttrOptions;
  /** Newest-first message list from `useCommunity` (lower index = newer). */
  messages: CommunityChatMessageEntity[];
  /** Current user's Firebase id — drives `isPeer` and the `"You"` label. */
  currentUserId: string;
  /** Hook callback — top sentinel intersected, load older page. */
  onLoadMore: () => void;
  /** Hook callback — user scrolled to the bottom (newest visible). */
  onScrolledToBottom: () => void;
  /** Hook callback — user scrolled away from the bottom. */
  onScrolledAway: () => void;
  /**
   * Set the message as the composer's reply target
   * (`useCommunity.setReplyTarget`).
   */
  onReplyTap: (message: CommunityChatMessageEntity) => void;
  /**
   * Toggle the like on a message (`useCommunity.likeMessage`). `liked` is the
   * NEW desired state emitted by the row's like button (`onToggle(!isLiked)`).
   */
  onLikeTap: (message: CommunityChatMessageEntity, liked: boolean) => void;
  /**
   * Open the full-screen media viewer for a ready image message
   * (`CommunityPage.handleMediaTap`). Threaded through to each row's media card.
   */
  onMediaTap: (message: CommunityChatMessageEntity) => void;
  /**
   * Join a live (`type = live`) message's session
   * (`CommunityPage.handleLiveItemTap`). Threaded through to each row's live
   * tile; inert once the session has ended.
   */
  onLiveItemTap: (message: CommunityChatMessageEntity) => void;
  /**
   * Sticky chrome (app bar above, composer below) overlapping the window
   * viewport — forwarded to `VirtualList`'s `reversedBottomPinInset` so the
   * short-list bottom-pin pads correctly (same contract as `ChatMessagesList`).
   */
  reversedBottomPinInset?: { top?: number; bottom?: number };
}

export const CommunityMessagesList = forwardRef<
  CommunityMessagesListHandle,
  CommunityMessagesListProps
>(({
  qa,
  messages,
  currentUserId,
  onLoadMore,
  onScrolledToBottom,
  onScrolledAway,
  onReplyTap,
  onLikeTap,
  onMediaTap,
  onLiveItemTap,
  reversedBottomPinInset,
}, ref) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { formatDate } = useDateFormatters();

  const virtualListRef = useRef<VirtualListHandle>(null);

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom: (behavior: ScrollBehavior = 'smooth') => {
        virtualListRef.current?.scrollToBottom(behavior);
      },
      scrollToTop: (behavior: ScrollBehavior = 'smooth') => {
        virtualListRef.current?.scrollToIndex(0, behavior);
      },
    }),
    [],
  );

  // Chronological order for `VirtualList(reversed)` (oldest at index 0, newest
  // at the end). See the index-space note in the file header. Memoized by
  // `messages` identity.
  const chronologicalMessages = useMemo(
    () => [...messages].reverse(),
    [messages],
  );

  // Stable key extractor — `id` is the message document id / optimistic UUID.
  const getItemKey = useCallback(
    (m: CommunityChatMessageEntity) => m.id,
    [],
  );

  const renderItem = useCallback(
    (message: CommunityChatMessageEntity) => (
      <CommunityMessageItem
        message={message}
        currentUserId={currentUserId}
        // `messageTime = DateUtils.getChatDate(createdTimestamp)`
        // (`community_screen.dart:1480`) — the web equivalent is the
        // locale-aware `formatDate` (see `MessageTimestamp.tsx:31-41`).
        timestampLabel={formatDate(message.createdTimestamp)}
        onReplyTap={onReplyTap}
        onLikeTap={onLikeTap}
        onMediaTap={onMediaTap}
        onLiveItemTap={onLiveItemTap}
      />
    ),
    [currentUserId, formatDate, onReplyTap, onLikeTap, onMediaTap, onLiveItemTap],
  );

  return (
    <div
      style={{
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        backgroundColor: theme.background,
      }}
    >
      <VirtualList<CommunityChatMessageEntity>
        ref={virtualListRef}
        qa={qa ?? { id: 'community-messages' }}
        items={chronologicalMessages}
        renderItem={renderItem}
        getItemKey={getItemKey}
        reversed
        reversedBottomPinInset={reversedBottomPinInset}
        gap={responsive.spacing.sm}
        defaultItemHeight={responsive.community.defaultItemHeight}
        onReachedEnd={onLoadMore}
        onScrolledToBottom={onScrolledToBottom}
        onScrolledAwayFromBottom={onScrolledAway}
        scrolledAwayFromBottomThreshold={responsive.community.scrolledAwayThreshold}
      />
    </div>
  );
});

CommunityMessagesList.displayName = 'CommunityMessagesList';
